<template>
  <div>
    <title-row :title="page.title" :breadcrumb-items="[
      { text: 'Tárolók', to: { name: 'AssetCabinetList' } },
      page.breadcrumbItem,
    ]">
      <template #buttons>
        <title-row-button v-if="hasRight('KEY_WRITE')" @click="save" title="Mentés">
          <v-icon dark>mdi-content-save</v-icon>
          Mentés
        </title-row-button>
      </template>
    </title-row>

    <loading-overlay v-if="loading" />

    <v-row>
      <v-col :cols="12">
        <v-card>
          <v-card-title>
            <v-icon left>mdi-file-cabinet</v-icon>
            Alapadatok
          </v-card-title>
          <v-card-text>
            <v-row>
              <v-col :cols="12" :md="6">
                <v-text-field label="Megnevezés" :error-messages="errors.name" v-model="form.name" filled />
                <v-autocomplete label="Épület" :items="buildings" item-text="name" item-value="id"
                  v-model="form.building_id" :value-comparator="$config.comparators.isEqual" filled clearable />
                <v-autocomplete label="Szint" :items="building_levels" item-text="name" item-value="id"
                  v-model="form.building_level_id" :disabled="!form.building_id"
                  :hint="!form.building_id ? 'Épület kiválasztása kötelező' : ''" persistent-hint
                  :value-comparator="$config.comparators.isEqual" filled clearable />
              </v-col>
              <v-col :cols="12" :md="6">
                <v-textarea label="Megjegyzés" name="name" v-model="form.remark" filled auto-grow
                  :rows="5"></v-textarea>

                <v-combobox v-model="form.emails" :items="userEmails" :search-input.sync="emailInput"
                  label="Értesítési email címek" hint="Email címek begépelése vagy kiválasztása a listából" clearable
                  hide-selected filled dense multiple small-chips deletable-chips item-text="email" item-value="email"
                  :menu-props="{ top: true, offsetY: true }" @keydown.enter.prevent="addChipFromCombobox"
                  @keydown.space.prevent="addChipFromCombobox">
                  <template #item="{ item }">
                    <v-list-item-content>
                      <v-list-item-title>{{ item.name }}</v-list-item-title>
                      <v-list-item-subtitle class="text--secondary">{{ item.email }}</v-list-item-subtitle>
                    </v-list-item-content>
                  </template>
                </v-combobox>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col :cols="12">
        <v-card>
          <v-card-title>
            <v-icon left>mdi-key-chain</v-icon>
            Kulcsok
          </v-card-title>
          <v-card-text>
            <v-two-panel-multiselect :headers="[
              { text: 'Név', value: 'name' },
              { text: 'Megjegyzés', value: 'remark' },
            ]" :options="{ sortBy: ['card_number'] }" :items="assets" v-model="form.assets" item-value="element_id">
              <template #header="{ search }">
                <v-card outlined class="mb-6">
                  <v-card-title>
                    <v-icon left>mdi-filter</v-icon>
                    Keresés
                  </v-card-title>
                  <v-card-text>
                    <v-row>
                      <v-col :cols="12" :md="6" :lg="3">
                        <v-text-field label="Név" v-model="search.name" hide-details filled />
                      </v-col>
                      <v-col :cols="12" :md="6" :lg="3">
                        <v-text-field label="Megjegyzés" v-model="search.remark" hide-details filled />
                      </v-col>
                    </v-row>
                  </v-card-text>
                </v-card>
              </template>
            </v-two-panel-multiselect>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </div>
</template>

<script>
export default {
  data() {
    return {
      form: {
        name: '',
        remark: '',
        cards: [],
        emails: [],
        building_id: 0,
      },

      assets: [],
      buildings: [],
      users: [],
      emailInput: '',

      errors: {},
      loading: false,
    };
  },

  async mounted() {
    this.fetchAll();
  },

  watch: {
    emailInput(val) {
      if (val && (val.endsWith(',') || val.endsWith(' '))) {
        this.addChipFromCombobox();
      }
    }
  },

  methods: {
    removeChip(i) {
      this.form.emails.splice(i, 1);
    },

    addChipFromCombobox() {
      if (this.emailInput && this.emailInput.trim()) {
        const email = this.emailInput.trim().replace(/[, ]+$/, '');
        if (email && !this.form.emails.includes(email)) {
          this.form.emails.push(email);
        }
        this.emailInput = '';
      }
    },

    async fetchAssets() {
      const response = await this.$http.post('assets/list', { limit: 100000 });
      this.assets = response.assets;
    },

    async fetchBuildings() {
      const response = await this.$http.post('buildings/list');
      this.buildings = response.buildings;
    },

    async fetchUsers() {
      try {
        const response = await this.$http.post('users/list');
        console.log(response);

        this.users = response.users || [];
      } catch (e) {
        console.error('Felhasználók betöltése sikertelen:', e);
        this.users = [];
      }
    },

    async fetchAll() {
      this.loading = true;
      await Promise.all([
        this.fetchAsset(),
        this.fetchAssets(),
        this.fetchBuildings(),
        this.fetchUsers()
      ]);
      this.loading = false;
    },

    async fetchAsset() {
      try {
        const response = await this.$http.post(
          `asset-cabinets/details/${this.$route.params.id || 0}`
        );
        if (response.asset_cabinet) {
          this.form = {
            ...this.form,
            ...response.asset_cabinet
          };
          if (!this.form.emails) {
            this.form.emails = [];
          }
        }
      } catch (e) {
        console.error(e);
      }
    },

    async save() {
      try {
        this.errors = {};
        this.loading = true;
        const response = await this.$http.post('asset-cabinets/save/', this.form);
        if (this.lodash.size(response.errors) > 0) {
          this.errors = response.errors;
          return;
        }

        if (response.status === 'OK') {
          this.$dialog.notify.success('Sikeres mentés');
          if (this.$route.name === 'NewAssetCabinet') {
            this.$router.push({
              name: 'AssetCabinetEditor',
              params: { id: response.asset_cabinet.id },
            });
          }
        }
      } catch (e) {
        console.error(e);
      } finally {
        this.loading = false;
      }
    },
  },

  asyncComputed: {
    building_levels: {
      async get() {
        if (!this.form.building_id) {
          return [];
        }
        try {
          const response = await this.$http.post('building-levels/list', {
            building_id: this.form.building_id,
          });
          return response.building_levels || [];
        } catch (e) {
          console.error('Building levels betöltése sikertelen:', e);
          return [];
        }
      },
      default: [],
    },
  },

  computed: {
    userEmails() {
      return this.users.map(user => ({
        email: user.email || user.name,
        name: user.name
      })).filter(user => user.email);
    },

    page() {
      switch (this.$route.name) {
        case 'NewAssetCabinet':
          return {
            isNew: true,
            title: 'Új kulcs/eszköz szekrény',
            breadcrumbItem: {
              text: 'Új kulcs/eszköz szekrény',
              disabled: true,
            },
          };
        case 'AssetCabinetEditor':
          return {
            isNew: false,
            title: `${this.form.name ?? 'Betöltés...'}`,
            breadcrumbItem: {
              text: this.form.name,
              disabled: true,
            },
          };
        default:
          return {};
      }
    },
  },
};
</script>
